import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Tooltip from "@material-ui/core/Tooltip";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";

import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import CardDetailDialog from "./CardDetailDialog";
import { formatCurrency, formatDate, formatDateTime } from "./format";
import { downloadCsv, csvNumber } from "./csv";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  card: {
    padding: theme.spacing(1.5),
    height: "100%",
    borderLeftWidth: 4,
    borderLeftStyle: "solid",
  },
  cardClickable: {
    cursor: "pointer",
    transition: theme.transitions.create(["box-shadow", "transform"], {
      duration: 150,
    }),
    "&:hover": {
      boxShadow: theme.shadows[4],
      transform: "translateY(-2px)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  cardHint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.hint,
    fontStyle: "italic",
  },
  cardValue: {
    fontWeight: "bold",
    lineHeight: 1.2,
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  credit: {
    color: "#2e7d32",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  debit: {
    color: "#9e9e9e",
    whiteSpace: "nowrap",
  },
  totalsRow: {
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#fafafa",
  },
}));

// Rótulos traduzidos na renderização/exportação para acompanhar a troca de idioma
const PAY_STATUS = {
  approved: { labelKey: "billingManager.paymentStatus.approved", color: "#4caf50" },
  pending: { labelKey: "billingManager.paymentStatus.pending", color: "#ff9800" },
  rejected: { labelKey: "billingManager.paymentStatus.rejected", color: "#f44336" },
  cancelled: { labelKey: "billingManager.paymentStatus.cancelled", color: "#9e9e9e" },
  refunded: { labelKey: "billingManager.paymentStatus.refunded", color: "#607d8b" },
};

const payStatusLabel = (status) =>
  PAY_STATUS[status] ? i18n.t(PAY_STATUS[status].labelKey) : status;

const methodLabel = (method) => {
  if (method === "pix") return "PIX";
  if (method === "credit_card") return i18n.t("billingManager.methods.creditCard");
  // "outros" é o agrupamento do backend para pagamentos sem forma registrada
  if (method === "outros") return i18n.t("billingManager.methods.other");
  return method;
};

const typeLabel = (type) => {
  if (type === "one_time") return i18n.t("billingManager.types.oneTime");
  if (type === "recurring") return i18n.t("billingManager.types.recurring");
  return type;
};

const today = () => moment().format("YYYY-MM-DD");

const PERIOD_PRESETS = [
  { value: "this_month", labelKey: "billingManager.statement.periods.thisMonth" },
  { value: "last_month", labelKey: "billingManager.statement.periods.lastMonth" },
  { value: "last_7", labelKey: "billingManager.statement.periods.last7" },
  { value: "last_30", labelKey: "billingManager.statement.periods.last30" },
  { value: "this_year", labelKey: "billingManager.statement.periods.thisYear" },
  { value: "custom", labelKey: "billingManager.statement.periods.custom" },
];

const rangeForPreset = (preset) => {
  switch (preset) {
    case "last_month":
      return {
        startDate: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        endDate: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    case "last_7":
      return {
        startDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
        endDate: today(),
      };
    case "last_30":
      return {
        startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
        endDate: today(),
      };
    case "this_year":
      return {
        startDate: moment().startOf("year").format("YYYY-MM-DD"),
        endDate: today(),
      };
    case "this_month":
    default:
      return {
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: today(),
      };
  }
};

const SummaryCard = ({ title, value, caption, color, classes, onClick }) => (
  <Paper
    variant="outlined"
    className={`${classes.card} ${onClick ? classes.cardClickable : ""}`}
    style={{ borderLeftColor: color }}
    onClick={onClick}
    onKeyDown={(e) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    <Typography variant="caption" color="textSecondary">
      {title}
    </Typography>
    <Typography variant="h6" className={classes.cardValue} style={{ color }}>
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {caption}
    </Typography>
    {onClick && (
      <Typography variant="caption" display="block" className={classes.cardHint}>
        {i18n.t("billingManager.statement.cardHint")}
      </Typography>
    )}
  </Paper>
);

export default function FinancialStatement() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(rangeForPreset("this_month"));
  const [view, setView] = useState("statement");
  const [statusFilter, setStatusFilter] = useState("approved");
  const [methodFilter, setMethodFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [order, setOrder] = useState("asc");
  const [onlyDueInPeriod, setOnlyDueInPeriod] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/statement", {
        params: { startDate: range.startDate, endDate: range.endDate },
      });
      setData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [range.startDate, range.endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreset = (value) => {
    setPreset(value);
    if (value !== "custom") setRange(rangeForPreset(value));
  };

  const summary = data?.summary;
  const entries = useMemo(() => data?.entries || [], [data]);
  const receivables = useMemo(() => data?.receivables || [], [data]);

  const companyOptions = useMemo(() => {
    const names = new Map();
    entries.forEach((e) => names.set(e.companyId, e.companyName));
    receivables.forEach((r) => names.set(r.companyId, r.companyName));
    return Array.from(names.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries, receivables]);

  // O popup detalha exatamente o que soma no card — por isso ignora os
  // filtros da tabela abaixo (situação/forma/empresa), que são outra visão.
  const periodLabel = data
    ? i18n.t("billingManager.statement.periodRange", {
        start: formatDate(data.period.startDate),
        end: formatDate(data.period.endDate),
      })
    : "";

  const openPaymentsDetail = (title, color, status) =>
    setDetail({
      kind: "payments",
      title,
      color,
      subtitle: i18n.t("billingManager.statement.periodSubtitle", { period: periodLabel }),
      rows: entries.filter((e) =>
        Array.isArray(status) ? status.includes(e.status) : e.status === status
      ),
    });

  const openReceivablesDetail = (title, color, situation) =>
    setDetail({
      kind: "receivables",
      title,
      color,
      subtitle: i18n.t("billingManager.statement.currentPositionSubtitle"),
      rows: receivables.filter((r) => r.situation === situation),
    });

  const visibleEntries = useMemo(() => {
    const rows = entries.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (methodFilter !== "all" && e.method !== methodFilter) return false;
      if (companyFilter !== "all" && String(e.companyId) !== String(companyFilter))
        return false;
      return true;
    });
    return order === "asc" ? rows : [...rows].reverse();
  }, [entries, statusFilter, methodFilter, companyFilter, order]);

  const visibleEntriesTotal = useMemo(
    () => visibleEntries.reduce((acc, e) => acc + Number(e.value || 0), 0),
    [visibleEntries]
  );

  const visibleReceivables = useMemo(() => {
    const situation = view === "overdue" ? "overdue" : "pending";
    return receivables.filter((r) => {
      if (r.situation !== situation) return false;
      if (onlyDueInPeriod && !r.dueInPeriod) return false;
      if (companyFilter !== "all" && String(r.companyId) !== String(companyFilter))
        return false;
      return true;
    });
  }, [receivables, view, onlyDueInPeriod, companyFilter]);

  const visibleReceivablesTotal = useMemo(
    () => visibleReceivables.reduce((acc, r) => acc + Number(r.value || 0), 0),
    [visibleReceivables]
  );

  const handleExport = () => {
    const periodo = i18n.t("billingManager.statement.export.period", {
      start: range.startDate,
      end: range.endDate,
    });
    if (view === "statement") {
      const statementFile = i18n.t("billingManager.statement.export.statementFile");
      downloadCsv(`${statementFile}_${periodo}.csv`, [
        [
          i18n.t("common.date"),
          i18n.t("common.company"),
          i18n.t("common.description"),
          i18n.t("billingManager.columns.method"),
          i18n.t("common.type"),
          i18n.t("billingManager.columns.status"),
          i18n.t("billingManager.columns.value"),
          i18n.t("billingManager.columns.balance"),
          i18n.t("billingManager.columns.mpPaymentId"),
        ],
        ...visibleEntries.map((e) => [
          formatDateTime(e.date),
          e.companyName,
          e.description,
          methodLabel(e.method),
          typeLabel(e.type),
          payStatusLabel(e.status),
          csvNumber(e.value),
          csvNumber(e.runningTotal),
          e.mpPaymentId || "",
        ]),
      ]);
      return;
    }
    const receivablesFile = i18n.t("billingManager.statement.export.receivablesFile");
    downloadCsv(`${receivablesFile}_${view}_${periodo}.csv`, [
      [
        i18n.t("billingManager.columns.dueDate"),
        i18n.t("common.company"),
        i18n.t("common.description"),
        i18n.t("billingManager.columns.daysOverdue"),
        i18n.t("billingManager.columns.value"),
        i18n.t("billingManager.columns.origin"),
      ],
      ...visibleReceivables.map((r) => [
        formatDate(r.dueDate),
        r.companyName,
        r.detail,
        r.daysOverdue || 0,
        csvNumber(r.value),
        r.source === "invoice"
          ? i18n.t("billingManager.origin.invoice")
          : i18n.t("billingManager.origin.companyDueDate"),
      ]),
    ]);
  };

  return (
    <div className={classes.root}>
      <Box className={classes.filters}>
        <TextField
          select
          size="small"
          variant="outlined"
          label={i18n.t("billingManager.statement.period")}
          value={preset}
          onChange={(e) => handlePreset(e.target.value)}
          style={{ minWidth: 170 }}
        >
          {PERIOD_PRESETS.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {i18n.t(p.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          type="date"
          label={i18n.t("common.from")}
          value={range.startDate}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            setPreset("custom");
            setRange((r) => ({ ...r, startDate: e.target.value }));
          }}
        />
        <TextField
          size="small"
          variant="outlined"
          type="date"
          label={i18n.t("common.to")}
          value={range.endDate}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            setPreset("custom");
            setRange((r) => ({ ...r, endDate: e.target.value }));
          }}
        />
        <TextField
          select
          size="small"
          variant="outlined"
          label={i18n.t("common.company")}
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <MenuItem value="all">{i18n.t("billingManager.all")}</MenuItem>
          {companyOptions.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" variant="outlined" onClick={load}>
          {i18n.t("common.refresh")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExport}
        >
          CSV
        </Button>
        {loading && <CircularProgress size={22} />}
      </Box>

      {summary && (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              classes={classes}
              color="#2e7d32"
              title={i18n.t("billingManager.statement.cards.received.title")}
              value={formatCurrency(summary.received.total)}
              caption={i18n.t("billingManager.statement.cards.received.caption", {
                total: summary.received.count,
                period: periodLabel,
              })}
              onClick={() =>
                openPaymentsDetail(
                  i18n.t("billingManager.statement.cards.received.title"),
                  "#2e7d32",
                  "approved"
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              classes={classes}
              color="#ff9800"
              title={i18n.t("billingManager.statement.cards.awaiting.title")}
              value={formatCurrency(summary.awaiting.total)}
              caption={i18n.t("billingManager.statement.cards.awaiting.caption", {
                total: summary.awaiting.count,
              })}
              onClick={() =>
                openPaymentsDetail(
                  i18n.t("billingManager.statement.cards.awaiting.title"),
                  "#ff9800",
                  "pending"
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              classes={classes}
              color="#1976d2"
              title={i18n.t("billingManager.statement.cards.pending.title")}
              value={formatCurrency(summary.pending.total)}
              caption={i18n.t("billingManager.statement.cards.pending.caption", {
                total: summary.pending.count,
              })}
              onClick={() =>
                openReceivablesDetail(
                  i18n.t("billingManager.statement.cards.pending.title"),
                  "#1976d2",
                  "pending"
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              classes={classes}
              color="#f44336"
              title={i18n.t("billingManager.statement.cards.overdue.title")}
              value={formatCurrency(summary.overdue.total)}
              caption={i18n.t("billingManager.statement.cards.overdue.caption", {
                total: summary.overdue.count,
              })}
              onClick={() =>
                openReceivablesDetail(
                  i18n.t("billingManager.statement.cards.overdue.title"),
                  "#f44336",
                  "overdue"
                )
              }
            />
          </Grid>
        </Grid>
      )}

      {summary && summary.failed.count > 0 && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          {i18n.t("billingManager.statement.failedSummary", {
            total: summary.failed.count,
            amount: formatCurrency(summary.failed.total),
          })}{" "}
          <Link
            component="button"
            variant="body2"
            onClick={() =>
              openPaymentsDetail(i18n.t("billingManager.statement.failedTitle"), "#f44336", [
                "rejected",
                "cancelled",
              ])
            }
          >
            {i18n.t("billingManager.statement.viewWhich")}
          </Link>
        </Typography>
      )}

      <Paper variant="outlined" style={{ marginBottom: 16 }}>
        <Tabs
          value={view}
          indicatorColor="primary"
          textColor="primary"
          onChange={(e, v) => setView(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={i18n.t("billingManager.statement.tabs.statement")} value="statement" />
          <Tab
            label={i18n.t("billingManager.statement.tabs.pending", {
              total: receivables.filter((r) => r.situation === "pending").length,
            })}
            value="pending"
          />
          <Tab
            label={i18n.t("billingManager.statement.tabs.overdue", {
              total: receivables.filter((r) => r.situation === "overdue").length,
            })}
            value="overdue"
          />
        </Tabs>
      </Paper>

      {view === "statement" ? (
        <>
          <Box className={classes.filters}>
            <TextField
              select
              size="small"
              variant="outlined"
              label={i18n.t("billingManager.columns.status")}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="approved">
                {i18n.t("billingManager.statement.statusFilter.approved")}
              </MenuItem>
              <MenuItem value="all">
                {i18n.t("billingManager.statement.statusFilter.all")}
              </MenuItem>
              <MenuItem value="pending">
                {i18n.t("billingManager.statement.statusFilter.pending")}
              </MenuItem>
              <MenuItem value="rejected">
                {i18n.t("billingManager.statement.statusFilter.rejected")}
              </MenuItem>
              <MenuItem value="refunded">
                {i18n.t("billingManager.statement.statusFilter.refunded")}
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={i18n.t("billingManager.columns.method")}
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <MenuItem value="all">{i18n.t("billingManager.all")}</MenuItem>
              <MenuItem value="pix">PIX</MenuItem>
              <MenuItem value="credit_card">
                {i18n.t("billingManager.methods.creditCard")}
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={i18n.t("billingManager.statement.order")}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              style={{ minWidth: 170 }}
            >
              <MenuItem value="asc">
                {i18n.t("billingManager.statement.orderOptions.asc")}
              </MenuItem>
              <MenuItem value="desc">
                {i18n.t("billingManager.statement.orderOptions.desc")}
              </MenuItem>
            </TextField>
            {summary?.byMethod?.length > 0 && (
              <Box display="flex" gridGap={8} alignItems="center" flexWrap="wrap">
                {summary.byMethod.map((m) => (
                  <Chip
                    key={m.name}
                    size="small"
                    variant="outlined"
                    label={`${methodLabel(m.name)}: ${formatCurrency(
                      m.total
                    )} (${m.count})`}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Paper variant="outlined" className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("common.date")}</TableCell>
                  <TableCell>{i18n.t("common.company")}</TableCell>
                  <TableCell>{i18n.t("common.description")}</TableCell>
                  <TableCell align="center">
                    {i18n.t("billingManager.columns.method")}
                  </TableCell>
                  <TableCell align="center">{i18n.t("common.type")}</TableCell>
                  <TableCell align="center">
                    {i18n.t("billingManager.columns.status")}
                  </TableCell>
                  <TableCell align="right">{i18n.t("billingManager.columns.value")}</TableCell>
                  <TableCell align="right">
                    {i18n.t("billingManager.columns.periodBalance")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleEntries.map((e) => {
                  const info = PAY_STATUS[e.status];
                  const isCredit = e.status === "approved";
                  return (
                    <TableRow key={e.id}>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(e.date)}
                      </TableCell>
                      <TableCell>{e.companyName}</TableCell>
                      <TableCell>
                        {e.description}
                        {e.statusDetail && !isCredit && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            {e.statusDetail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {methodLabel(e.method) || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {typeLabel(e.type)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={e.mpPaymentId ? `MP ${e.mpPaymentId}` : ""}>
                          <Chip
                            size="small"
                            label={payStatusLabel(e.status)}
                            style={{
                              backgroundColor: info?.color || "#9e9e9e",
                              color: "#fff",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        className={isCredit ? classes.credit : classes.debit}
                      >
                        {isCredit ? "+ " : ""}
                        {formatCurrency(e.value)}
                      </TableCell>
                      <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                        {formatCurrency(e.runningTotal)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleEntries.length > 0 && (
                  <TableRow className={classes.totalsRow}>
                    <TableCell colSpan={6}>
                      <strong>
                        {i18n.t("billingManager.statement.totalShown", {
                          count: visibleEntries.length,
                        })}
                      </strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(visibleEntriesTotal)}</strong>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                {!loading && visibleEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("billingManager.statement.noTransactions")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      ) : (
        <>
          <Box className={classes.filters}>
            <TextField
              select
              size="small"
              variant="outlined"
              label={i18n.t("billingManager.columns.dueDate")}
              value={onlyDueInPeriod ? "period" : "all"}
              onChange={(e) => setOnlyDueInPeriod(e.target.value === "period")}
              style={{ minWidth: 220 }}
            >
              <MenuItem value="all">
                {i18n.t("billingManager.statement.dueFilter.all")}
              </MenuItem>
              <MenuItem value="period">
                {i18n.t("billingManager.statement.dueFilter.period")}
              </MenuItem>
            </TextField>
          </Box>

          <Paper variant="outlined" className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("billingManager.columns.dueDate")}</TableCell>
                  <TableCell>{i18n.t("common.company")}</TableCell>
                  <TableCell>{i18n.t("common.description")}</TableCell>
                  <TableCell align="center">
                    {i18n.t("billingManager.columns.status")}
                  </TableCell>
                  <TableCell align="right">{i18n.t("billingManager.columns.value")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReceivables.map((r) => (
                  <TableRow key={`${r.source}-${r.invoiceId || r.companyId}`}>
                    <TableCell
                      style={
                        r.daysOverdue > 0
                          ? { color: "#f44336", fontWeight: "bold", whiteSpace: "nowrap" }
                          : { whiteSpace: "nowrap" }
                      }
                    >
                      {formatDate(r.dueDate)}
                    </TableCell>
                    <TableCell>{r.companyName}</TableCell>
                    <TableCell>
                      {r.detail}
                      {r.source === "company" && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {i18n.t("billingManager.statement.estimatedValue")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {r.daysOverdue > 0 ? (
                        <Chip
                          size="small"
                          label={i18n.t("billingManager.daysOverdue", {
                            count: r.daysOverdue,
                          })}
                          style={{ backgroundColor: "#f44336", color: "#fff" }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={i18n.t("billingManager.notDueYet")}
                          style={{ backgroundColor: "#1976d2", color: "#fff" }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                      {formatCurrency(r.value)}
                    </TableCell>
                  </TableRow>
                ))}
                {visibleReceivables.length > 0 && (
                  <TableRow className={classes.totalsRow}>
                    <TableCell colSpan={4}>
                      <strong>
                        {i18n.t("billingManager.statement.totalCharges", {
                          count: visibleReceivables.length,
                        })}
                      </strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(visibleReceivablesTotal)}</strong>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && visibleReceivables.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {view === "overdue"
                          ? i18n.t("billingManager.statement.nothingOverdue")
                          : i18n.t("billingManager.statement.nothingPending")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <CardDetailDialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail?.title}
        subtitle={detail?.subtitle}
        color={detail?.color}
        kind={detail?.kind}
        rows={detail?.rows}
      />
    </div>
  );
}
